import { createContext, useContext, useState, useEffect } from 'react';
import { settingsApi } from '@/lib/api';
import { useLanguage } from './LanguageContext';

const SiteSettingsContext = createContext();

export function SiteSettingsProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await settingsApi.get();
      setSettings(response.data);
    } catch (error) {
      console.error('Error loading settings:', error);
      // Set defaults
      setSettings({
        site_name: 'Tim Automobile',
        site_tagline: 'Premium Fahrzeuge',
        site_tagline_en: 'Premium Vehicles',
        nav_home: 'Home',
        nav_home_en: 'Home',
        nav_inventory: 'Fahrzeuge',
        nav_inventory_en: 'Vehicles',
        nav_about: 'Über uns',
        nav_about_en: 'About Us',
        nav_contact: 'Kontakt',
        nav_contact_en: 'Contact',
        nav_favorites: 'Merkliste',
        nav_favorites_en: 'Favorites'
      });
    } finally {
      setLoading(false);
    }
  };

  const refreshSettings = () => loadSettings();

  return (
    <SiteSettingsContext.Provider value={{ settings, loading, refreshSettings }}>
      {children}
    </SiteSettingsContext.Provider>
  );
}

export function useSiteSettings() {
  const context = useContext(SiteSettingsContext);
  if (!context) {
    throw new Error('useSiteSettings must be used within SiteSettingsProvider');
  }
  return context;
}

// Hook that returns translated settings based on current language
export function useTranslatedSettings() {
  const { settings, loading, refreshSettings } = useSiteSettings();
  const { language } = useLanguage();
  
  // Helper function to get the correct language version
  const getText = (key) => {
    if (!settings) return '';
    
    // If English, try to get the _en version first
    if (language === 'en') {
      const enKey = `${key}_en`;
      if (settings[enKey]) {
        return settings[enKey];
      }
    }
    
    // Return German (default) version
    return settings[key] || '';
  };
  
  // Return a proxy-like object that automatically translates
  const translatedSettings = settings ? {
    ...settings,
    // Header
    header_title: getText('header_title'),
    header_subtitle: getText('header_subtitle'),
    site_tagline: getText('site_tagline'),
    // Navigation
    nav_home: getText('nav_home'),
    nav_inventory: getText('nav_inventory'),
    nav_about: getText('nav_about'),
    nav_contact: getText('nav_contact'),
    nav_favorites: getText('nav_favorites'),
    // About
    about_title: getText('about_title'),
    about_subtitle: getText('about_subtitle'),
    about_text: getText('about_text'),
    stat_years_label: getText('stat_years_label'),
    stat_cars_label: getText('stat_cars_label'),
    stat_satisfaction_label: getText('stat_satisfaction_label'),
    // Video
    video_title: getText('video_title'),
    video_subtitle: getText('video_subtitle'),
    // Contact
    contact_title: getText('contact_title'),
    contact_subtitle: getText('contact_subtitle'),
    contact_text: getText('contact_text'),
    // Footer
    footer_description: getText('footer_description'),
    footer_hours_weekday: getText('footer_hours_weekday'),
    footer_hours_saturday: getText('footer_hours_saturday'),
    footer_hours_sunday: getText('footer_hours_sunday'),
    // SEO
    seo_title: getText('seo_title'),
    seo_description: getText('seo_description'),
    seo_keywords: getText('seo_keywords'),
    // Auto-reply
    auto_reply_message: getText('auto_reply_message'),
    admin_offline_message: getText('admin_offline_message'),
    // Financing
    financing_partner_text: getText('financing_partner_text'),
    // Featured Cars
    featured_title: getText('featured_title'),
    featured_subtitle: getText('featured_subtitle'),
    featured_view_all: getText('featured_view_all'),
  } : null;
  
  return { settings: translatedSettings, rawSettings: settings, loading, refreshSettings, getText };
}
